'use client'

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

const TOAST_MS = 4_000

type ToastApi = (message: string) => void

const ToastContext = createContext<ToastApi | null>(null)

/** A short message at the bottom of the screen for a change the page does not show by itself. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null)
  const show = useCallback<ToastApi>((message) => setToast({ id: Date.now(), message }), [])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [toast])

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast && (
        <output
          key={toast.id}
          className="fixed bottom-4 left-1/2 z-[60] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground shadow-lg"
        >
          {toast.message}
        </output>
      )}
    </ToastContext.Provider>
  )
}

/** Shows a toast; does nothing where there is no provider, so a shared component works anywhere. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? (() => undefined)
}
